import React, { useEffect, useMemo, useState } from 'react';
import { Check, Info } from 'react-feather';
import { Button, Field, Select, TextInput } from '../../../ui';
import {
  FieldValues,
  FOLLOW_UP_ACTIVITY,
  fieldDepth,
  fieldVisible,
  isEmptyAnswer,
  pruneHiddenAnswers,
  STAGE_LABEL,
  summarizeAnswers,
  TaskStage,
  TicketConfig,
  TicketFieldDef,
  TicketResolution,
} from '../../../Tasks/tasksApi';
import { Callout } from './ui-pending';
import './TicketPanel.scss';

/**
 * UI for task types on the unified ticket model (Open → In Progress →
 * Closed): what the RM logs, the dropdowns that open as they answer, and
 * the closing reasons. Everything comes from the type's ticketConfig — no
 * task type is hardcoded here.
 */

/**
 * Where the ticket stands: a progress indicator, not controls. Stages move
 * by logging an activity or closing, so this deliberately avoids anything
 * button-shaped — dots on a line, colour only on the step you're at.
 */
export const StageTrack: React.FC<{ stage: TaskStage; closedLabel?: string; tone?: TicketResolution['tone'] }> = ({
  stage,
  closedLabel,
  tone,
}) => {
  const order: TaskStage[] = ['OPEN', 'IN_PROGRESS', 'CLOSED'];
  const current = order.indexOf(stage);
  // Closed tickets colour the current step by outcome; otherwise it's the brand colour.
  const nowTone = stage === 'CLOSED' ? tone ?? 'success' : 'active';
  return (
    <ol className={`tp-stage tp-stage--${nowTone}`} aria-label="Stage">
      {order.map((s, i) => {
        const done = i < current;
        const now = i === current;
        const state = now ? 'now' : done ? 'done' : 'todo';
        return (
          <React.Fragment key={s}>
            <li className={`tp-stage__step tp-stage__step--${state}`} aria-current={now ? 'step' : undefined}>
              <span className="tp-stage__dot" aria-hidden="true" />
              <span className="tp-stage__label">
                {s === 'CLOSED' && now && closedLabel ? closedLabel : STAGE_LABEL[s]}
              </span>
            </li>
            {i < order.length - 1 && (
              <span aria-hidden="true" className={`tp-stage__line${i < current ? ' tp-stage__line--done' : ''}`} />
            )}
          </React.Fragment>
        );
      })}
    </ol>
  );
};

/** "Captured on this ticket" — the answers so far, cascades folded in. */
export const CapturedAnswers: React.FC<{ cfg: TicketConfig; values: FieldValues }> = ({ cfg, values }) => {
  const lines = summarizeAnswers(cfg, values);
  if (!lines.length) return null;
  return (
    <div className="tp-captured">
      <div className="tp-captured__title">Captured on this ticket</div>
      {lines.map((l) => (
        <div key={l.label} className="tp-captured__row">
          <span className="tp-captured__label">{l.label}</span>
          <span className="ds-strong">{l.value}</span>
        </div>
      ))}
    </div>
  );
};

/**
 * Inputs for a set of fields, with cascades: a follow-up dropdown appears
 * (indented, "because …") only once its parent's answer calls for it, and is
 * dropped again if the parent changes.
 */
/**
 * Is this field required *here*? `requiredAt` narrows `required` to some of
 * the activities that ask it. The marker and the validation both read this,
 * so a form cannot show an asterisk it does not enforce.
 */
/**
 * The furthest date a field will accept, as YYYY-MM-DD, or undefined when it
 * sets no horizon. Mirrors the server's `maxMonthsAhead` check so the picker
 * and the API agree — "Come back on" caps at six months, because past that a
 * learner is declining a renewal rather than parking one.
 */
const maxDateFor = (f: TicketFieldDef): string | undefined => {
  if (f.maxMonthsAhead === undefined) return undefined;
  const limit = new Date();
  limit.setMonth(limit.getMonth() + f.maxMonthsAhead);
  return limit.toISOString().slice(0, 10);
};

const isRequiredAt = (f: TicketFieldDef, askAt: string): boolean =>
  !!f.required && (!f.requiredAt || f.requiredAt.includes(askAt));

export const TicketFieldInputs: React.FC<{
  cfg: TicketConfig;
  fields: TicketFieldDef[];
  values: FieldValues;
  onChange: (next: FieldValues) => void;
  idPrefix: string;
  /**
   * Where these fields are being asked: an activity key, or `res:<key>` when
   * closing. Needed because `requiredAt` can make a field required in one
   * place and optional in another — without it the asterisk says required
   * while nothing enforces it, which reads as a broken form.
   */
  askAt: string;
}> = ({ cfg, fields, values, onChange, idPrefix, askAt }) => {
  const set = (key: string, value: string | string[]): void => {
    const next = { ...values };
    if (isEmptyAnswer(value)) delete next[key];
    else next[key] = value;
    onChange(pruneHiddenAnswers(cfg, next));
  };
  return (
    <>
      {fields
        .filter((f) => fieldVisible(cfg, f, values))
        .map((f) => {
          const depth = fieldDepth(cfg, f);
          const id = `${idPrefix}-${f.key}`;
          const parent = f.showWhen ? cfg.fields.find((p) => p.key === f.showWhen!.field) : undefined;
          const parentValue = parent ? values[parent.key] : undefined;
          const because =
            parent && typeof parentValue === 'string'
              ? parent.options?.find((o) => o.key === parentValue)?.label ?? parentValue
              : undefined;
          const value = values[f.key];
          const isDate = f.kind === 'date' || f.kind === 'datetime';
          return (
            <div
              key={f.key}
              className={`tp-field${depth ? ' tp-field--nested' : ''}`}
              // Cascade indent grows with the field's depth in the config.
              style={depth ? { marginLeft: (depth - 1) * 14 + 4 } : undefined}
            >
              {because && <span className="tp-field__because">because {because}</span>}
              <Field
                id={id}
                required={isRequiredAt(f, askAt)}
                label={
                  <>
                    {f.label}
                    {f.kind === 'multi' && <span className="tp-field__hint"> · pick any</span>}
                  </>
                }
              >
                {(control) => (
                  <>
                    {f.kind === 'select' && (
                      <Select
                        {...control}
                        size="sm"
                        value={typeof value === 'string' ? value : ''}
                        onChange={(e) => set(f.key, e.target.value)}
                        options={[
                          { value: '', label: 'Choose…' },
                          ...(f.options ?? []).map((o) => ({ value: o.key, label: o.label })),
                        ]}
                      />
                    )}
                    {f.kind === 'multi' && (
                      <div id={control.id} role="group" aria-label={f.label} className="tp-chips">
                        {f.options?.map((o) => {
                          const list = Array.isArray(value) ? value : [];
                          const on = list.includes(o.key);
                          return (
                            <button
                              key={o.key}
                              type="button"
                              aria-pressed={on}
                              className={`tp-chip${on ? ' tp-chip--on' : ''}`}
                              onClick={() => set(f.key, on ? list.filter((k) => k !== o.key) : [...list, o.key])}
                            >
                              {on && <Check size={12} aria-hidden />}
                              {o.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {(f.kind === 'text' || isDate) && (
                      <TextInput
                        {...control}
                        type={f.kind === 'date' ? 'date' : f.kind === 'datetime' ? 'datetime-local' : 'text'}
                        size="sm"
                        className={isDate ? 'tp-input--date' : undefined}
                        placeholder={f.kind === 'text' ? 'Type here…' : undefined}
                        // The server rejects a date past the field's horizon.
                        // Capping the picker means the RM never picks one and
                        // then reads a 400 they had no way to see coming.
                        max={f.kind === 'date' ? maxDateFor(f) : undefined}
                        value={typeof value === 'string' ? value : ''}
                        onChange={(e) => set(f.key, e.target.value)}
                      />
                    )}
                  </>
                )}
              </Field>
            </div>
          );
        })}
    </>
  );
};

/**
 * Kept for compatibility; the note rows now share the `.tp-note-btn` class
 * (TicketPanel.scss) instead of this inline style.
 */
export const NOTE_BUTTON: React.CSSProperties = { flexShrink: 0, minWidth: 128, whiteSpace: 'nowrap' };

/**
 * `askAt` is the activity key, or `res:<key>` when closing — a field can be
 * asked in several places and required in only some of them. The concern
 * reason is the case: required on the call, offered but not insisted on when
 * raising the FD ticket or a follow-up, since a follow-up can come out of an
 * attempt nobody answered. Mirrors applyFieldAnswers on the server, which is
 * what actually enforces it.
 */
const missingRequired = (
  cfg: TicketConfig,
  fields: TicketFieldDef[],
  values: FieldValues,
  askAt: string,
): string[] =>
  fields
    .filter((f) => isRequiredAt(f, askAt) && fieldVisible(cfg, f, values) && isEmptyAnswer(values[f.key]))
    .map((f) => f.label);

/** Pick an activity, answer what it asks, add a note, log it. */
export const ActivityComposer: React.FC<{
  cfg: TicketConfig;
  answers: FieldValues;
  busy: boolean;
  onLog: (input: {
    activityKey: string;
    fieldValues: FieldValues;
    followUpAt?: string;
    note?: string;
  }) => Promise<boolean>;
}> = ({ cfg, answers, busy, onLog }) => {
  // Nothing is picked until the RM picks it. Defaulting to "Connected"
  // made a ticket read as though the learner had already been reached —
  // the dropdown sits above the timeline, so anyone not scrolling saw a
  // connected call that never happened.
  const [activity, setActivity] = useState('');
  const asked = useMemo(() => cfg.fields.filter((f) => f.askAt.includes(activity)), [cfg, activity]);
  const prefill = (): FieldValues => {
    const v: FieldValues = {};
    asked.forEach((f) => {
      if (!isEmptyAnswer(answers[f.key])) v[f.key] = answers[f.key];
    });
    return v;
  };
  const [values, setValues] = useState<FieldValues>(prefill);
  const [followUpAt, setFollowUpAt] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  // Switching activity (or new answers arriving after a save) re-seeds the
  // form from what the ticket already holds. Keyed on the answers' content,
  // not identity — the page re-renders on a timer and must not wipe a
  // half-filled form.
  const answersKey = JSON.stringify(answers);
  useEffect(() => {
    setValues(prefill());
    setError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity, answersKey]);

  // Which activities take a date comes from the config. The fallback keeps a
  // backend that predates the marker behaving as it did.
  const activityDef = cfg.activities.find((a) => a.key === activity);
  const followUpMode =
    activityDef?.followUp ?? (activity === FOLLOW_UP_ACTIVITY ? 'required' : undefined);
  const asksFollowUp = !!followUpMode;

  const submit = async (): Promise<void> => {
    const missing = missingRequired(cfg, asked, values, activity);
    if (followUpMode === 'required' && !followUpAt) missing.unshift('Follow-up date & time');
    if (missing.length) {
      setError(`Fill in: ${missing.join(', ')}`);
      return;
    }
    // Send every asked field, empties included, so a cleared answer clears.
    const payload: FieldValues = {};
    asked.forEach((f) => {
      if (fieldVisible(cfg, f, values)) payload[f.key] = values[f.key] ?? '';
    });
    const ok = await onLog({
      activityKey: activity,
      fieldValues: payload,
      followUpAt: asksFollowUp && followUpAt ? new Date(followUpAt).toISOString() : undefined,
      note: note.trim() || undefined,
    });
    if (ok) {
      setNote('');
      setFollowUpAt('');
      setError('');
      setActivity(''); // back to nothing picked, so the next one is deliberate too
    }
  };

  return (
    <div className="tp-composer">
      <div className="tp-composer__row">
        <Field label="Activity" className="tp-composer__activity">
          {(control) => (
            <Select
              {...control}
              size="sm"
              value={activity}
              onChange={(e) => setActivity(e.target.value)}
              options={[
                { value: '', label: 'Select activity…' },
                ...cfg.activities.map((a) => ({ value: a.key, label: a.label })),
              ]}
            />
          )}
        </Field>
        {asksFollowUp && (
          <Field
            label={followUpMode === 'required' ? 'Follow-up date & time' : 'Remind me (optional)'}
            className="tp-composer__followup"
          >
            {(control) => (
              <TextInput
                {...control}
                type="datetime-local"
                size="sm"
                value={followUpAt}
                onChange={(e) => setFollowUpAt(e.target.value)}
              />
            )}
          </Field>
        )}
      </div>
      {asked.length > 0 && (
        <div className="tp-answers">
          <span className="tp-answers__title">
            {activity === 'connected' ? 'What did the learner say?' : 'Details'}
          </span>
          <TicketFieldInputs cfg={cfg} fields={asked} values={values} onChange={setValues} idPrefix="act" askAt={activity} />
        </div>
      )}
      {/* Same shape as "Add a note": the note takes the width, one fixed button. */}
      <div className="tp-note-row">
        <textarea
          className="ds-input tp-textarea"
          rows={2}
          aria-label="Activity note"
          placeholder="Note (optional) — what was discussed, next step…"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <Button
          variant="primary"
          size="sm"
          className="tp-note-btn"
          icon={<Check size={14} aria-hidden />}
          disabled={busy || !activity}
          onClick={submit}
        >
          Log activity
        </Button>
      </div>
      {error && <Callout tone="danger">{error}</Callout>}
    </div>
  );
};

/**
 * Closing reasons as buttons. A reason that needs an answer first is
 * disabled with the reason why; one that asks something on close (e.g. the
 * course start date) opens a small confirm form; the rest close in one click.
 */
export const ClosePicker: React.FC<{
  cfg: TicketConfig;
  answers: FieldValues;
  busy: boolean;
  onClose: (input: { resolutionKey: string; fieldValues?: FieldValues; note?: string }) => Promise<boolean>;
}> = ({ cfg, answers, busy, onClose }) => {
  const [pending, setPending] = useState<TicketResolution | null>(null);
  const [values, setValues] = useState<FieldValues>({});
  const [error, setError] = useState('');

  const askedOnClose = (r: TicketResolution): TicketFieldDef[] =>
    cfg.fields.filter((f) => f.askAt.includes(`res:${r.key}`));
  const missingFor = (r: TicketResolution): string[] =>
    (r.requires ?? [])
      .filter((k) => isEmptyAnswer(answers[k]))
      .map((k) => cfg.fields.find((f) => f.key === k)?.label ?? k);

  const pick = async (r: TicketResolution): Promise<void> => {
    if (askedOnClose(r).length) {
      setPending(r);
      setValues({});
      setError('');
      return;
    }
    await onClose({ resolutionKey: r.key });
  };

  const confirm = async (): Promise<void> => {
    if (!pending) return;
    const missing = missingRequired(cfg, askedOnClose(pending), values, `res:${pending.key}`);
    if (missing.length) {
      setError(`Fill in: ${missing.join(', ')}`);
      return;
    }
    if (await onClose({ resolutionKey: pending.key, fieldValues: values })) setPending(null);
  };

  if (pending) {
    return (
      <div className="tp-close-form">
        <span>
          Close as <strong>{pending.label}</strong>
          {pending.spawnsFollowOn && (
            <span className="ds-text-muted"> — a new ticket opens for the learner on that date</span>
          )}
        </span>
        <TicketFieldInputs
          cfg={cfg}
          fields={askedOnClose(pending)}
          values={values}
          onChange={setValues}
          idPrefix="close"
          askAt={`res:${pending.key}`}
        />
        {error && <Callout tone="danger">{error}</Callout>}
        <div className="rm-row">
          <Button size="sm" variant="secondary" onClick={() => setPending(null)}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" disabled={busy} onClick={confirm}>
            Close ticket
          </Button>
        </div>
      </div>
    );
  }

  // A retired reason still labels the tickets that closed with it, but is no
  // longer something an RM can choose.
  const offered = cfg.resolutions.filter((r) => !r.retired);
  const blocked = offered
    .map((r) => ({ r, missing: missingFor(r) }))
    .filter((x) => x.missing.length);

  return (
    <div className="tp-close">
      <div className="tp-close__reasons">
        {offered.map((r) => {
          const missing = missingFor(r);
          return (
            <Button
              key={r.key}
              size="sm"
              variant="secondary"
              className={`tp-res tp-res--${r.tone}`}
              disabled={busy || missing.length > 0}
              title={
                missing.length
                  ? `Capture ${missing.join(', ')} first`
                  : r.systemOutcome
                    ? 'Normally set by the system — picking it here is recorded as done by you'
                    : `Close as ${r.label}`
              }
              onClick={() => pick(r)}
            >
              {r.label}
              {r.systemOutcome && <span className="tp-res__system">(system)</span>}
            </Button>
          );
        })}
      </div>
      {blocked.length > 0 && (
        <span className="tp-blocked">
          <Info size={14} aria-hidden />
          <span>
            {(() => {
              // One line, grouped by what's missing: "To close, log Connected
              // with the Refund category first." — naming the blocked reasons
              // only when some others can already be used.
              const missing = Array.from(new Set(blocked.flatMap((b) => b.missing)));
              const all = blocked.length === offered.length;
              const what = missing.join(' and ');
              return all ? (
                <>To close, log <strong>Connected</strong> with the {what} first.</>
              ) : (
                <>
                  {blocked.map((b) => b.r.label).join(', ')} {blocked.length === 1 ? 'needs' : 'need'} the {what} — log{' '}
                  <strong>Connected</strong> with it first.
                </>
              );
            })()}
          </span>
        </span>
      )}
    </div>
  );
};
